import { defineStore } from 'pinia'
import { useAppStore } from './app'

export const useDeveloperStore = defineStore('developer', {
  state: () => ({
    // Tokens criados pelo desenvolvedor
    myTokens: [],
    selectedToken: null,
    
    // Contratos implantados
    myContracts: [],
    selectedContract: null,
    
    // Editor de código (IDE)
    editorContent: '',
    editorLanguage: 'queen', // Linguagem padrão Queen
    compilationResult: null,
    
    // Assistente IA
    assistantActive: false,
    assistantMessages: [],
    assistantLoading: false,
    
    // Estado de carregamento
    isLoadingTokens: false,
    isCompiling: false
  }),
  
  getters: {
    // Getters relacionados a tokens
    getTokenById: (state) => (id) => {
      return state.myTokens.find(token => token.id === id) || null
    },
    
    hasTokens: (state) => state.myTokens.length > 0,
    
    // Getters relacionados ao editor
    hasCompilationErrors: (state) => {
      if (!state.compilationResult) return false
      return state.compilationResult.errors && state.compilationResult.errors.length > 0
    },
    
    hasCompilationWarnings: (state) => {
      if (!state.compilationResult) return false
      return state.compilationResult.warnings && state.compilationResult.warnings.length > 0
    },
    
    // Getters relacionados ao assistente
    hasActiveAssistantConversation: (state) => {
      return state.assistantMessages.length > 0
    }
  },
  
  actions: {
    // Ações para gerenciar tokens
    async fetchTokens() {
      return await this.fetchMyTokens();
    },
    
    async fetchContracts() {
      const appStore = useAppStore()
      appStore.setLoading(true)
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.contratos.listar()
        this.myContracts = Array.isArray(data) ? data : (data?.contracts || [])
      } catch (error) {
        console.warn('Erro ao carregar contratos:', error.message)
        // Keep existing data on network error, don't clear
        if (this.myContracts.length === 0) {
          this.myContracts = []
        }
      } finally {
        appStore.setLoading(false)
      }
    },
    
    async fetchMyTokens() {
      const appStore = useAppStore()
      this.isLoadingTokens = true
      appStore.setLoading(true)
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.contratos.listar()
        const contracts = Array.isArray(data) ? data : (data?.contracts || [])
        // Filter for token contracts
        this.myTokens = contracts.filter(c => c.type === 'token' || c.isToken)
      } catch (error) {
        console.warn('Erro ao carregar tokens:', error.message)
        if (this.myTokens.length === 0) {
          this.myTokens = []
        }
      } finally {
        this.isLoadingTokens = false
        appStore.setLoading(false)
      }
    },
    
    selectToken(tokenId) {
      this.selectedToken = this.getTokenById(tokenId)
    },
    
    // Ações para o editor de código
    updateEditorContent(content) {
      this.editorContent = content
    },
    
    setEditorLanguage(language) {
      this.editorLanguage = language
    },
    
    async compileCode() {
      if (!this.editorContent.trim()) {
        return
      }
      
      const appStore = useAppStore()
      this.isCompiling = true
      appStore.setLoading(true)
      
      try {
        // Compilação real via API do kernel
        const { ExplorerAPI } = await import('@/services/api')
        const resultado = await ExplorerAPI.compilador.compilar(
          this.editorContent,
          this.currentFile?.name?.replace('.qtz', '') || 'Contract'
        )
        
        if (resultado.status === 'compiled' && resultado.wasm_base64) {
          this.compilationResult = {
            success: true,
            errors: [],
            warnings: [],
            bytecode: resultado.wasm_base64,
            wasmSize: resultado.wasm_size || 0,
          }
          
          appStore.addNotification({
            type: 'success',
            message: `Compilação concluída: ${resultado.wasm_size || 0} bytes WASM`,
            timeout: 5000
          })
        } else {
          this.compilationResult = {
            success: false,
            errors: [
              {
                line: 1,
                message: resultado.errors || resultado.output || 'Erro de compilação',
                severity: 'error'
              }
            ],
            warnings: [],
            bytecode: null
          }
          
          appStore.addNotification({
            type: 'error',
            message: 'Compilação falhou',
            timeout: 5000
          })
        }
        
      } catch (error) {
        console.error('Erro na compilação:', error)
        appStore.addNotification({
          type: 'error',
          message: 'Erro ao processar a compilação',
          timeout: 5000
        })
        
        this.compilationResult = {
          success: false,
          errors: [
            {
              line: 0,
              message: 'Erro interno no compilador',
              severity: 'error'
            }
          ],
          warnings: [],
          bytecode: null
        }
      } finally {
        this.isCompiling = false
        appStore.setLoading(false)
      }
    },
    
    // Ações para o assistente IA
    toggleAssistant() {
      this.assistantActive = !this.assistantActive
    },
    
    async sendMessageToAssistant(message) {
      if (!message.trim()) return
      
      const appStore = useAppStore()
      
      // Adiciona mensagem do usuário ao histórico
      this.assistantMessages.push({
        id: Date.now(),
        role: 'user',
        content: message,
        timestamp: new Date()
      })
      
      this.assistantLoading = true
      
      try {
        // Simulando chamada à API do assistente
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // Resposta contextual do assistente baseada em dados reais
        let responseText = ''
        const pergunta = message.toLowerCase()
        
        if (pergunta.includes('token') || pergunta.includes('qrc')) {
          responseText = 'Para criar um token QRC-21 na QuarnX, implemente as funções transfer(), balanceOf() e totalSupply(). Use blocos verify { requires: ...; ensures: ...; } para verificação formal.'
        } else if (pergunta.includes('deploy') || pergunta.includes('implantar')) {
          responseText = 'Para fazer deploy: 1) Compile seu contrato Queen (gera WASM), 2) Clique em "Implantar" para enviar ao kernel. O contrato recebe um endereço único na blockchain.'
        } else if (pergunta.includes('segurança') || pergunta.includes('vulnerabilidade') || pergunta.includes('erro')) {
          responseText = 'Use blocos verify { requires: condição; ensures: resultado; } para verificação formal. Siga o padrão checks-effects-interactions contra reentrância. Adicione controle de acesso com modifier.'
        } else if (pergunta.includes('compilar') || pergunta.includes('wasm')) {
          responseText = 'O compilador Queen gera WASM a partir do código fonte. Clique em "Compilar" no topo da página. O WASM é validado automaticamente (magic number, exports).'
        } else {
          responseText = 'Posso ajudar com: criação de tokens QRC-21, deploy de contratos, segurança e verificação formal, compilação Queen→WASM. O que você precisa?'
        }
        
        const randomResponse = responseText
        
        // Adiciona resposta do assistente ao histórico
        this.assistantMessages.push({
          id: Date.now(),
          role: 'assistant',
          content: randomResponse,
          timestamp: new Date()
        })
        
      } catch (error) {
        console.error('Erro ao comunicar com o assistente:', error)
        appStore.addNotification({
          type: 'error',
          message: 'Falha na comunicação com o assistente Queen',
          timeout: 5000
        })
        
        // Mensagem de erro do assistente
        this.assistantMessages.push({
          id: Date.now(),
          role: 'assistant',
          content: "Desculpe, estou enfrentando problemas de comunicação no momento. Por favor, tente novamente em alguns instantes.",
          timestamp: new Date(),
          isError: true
        })
      } finally {
        this.assistantLoading = false
      }
    },
    
    clearAssistantConversation() {
      this.assistantMessages = []
    }
  }
})
